import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DRAW_EXCEPTION_ACTIONS, type DrawExceptionAction, type PlannedDraw, type ProductDetailView, type ProductVersionView } from '@lp/contracts';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { del, errorText, fieldProblems, get, patch, post } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { PlannedDraws, ProductConfigView } from '@/components/product-config';
import { RenameDialog } from '@/components/rename-dialog';
import { Badge, Button, Card, ErrorBox, Field, Input, Loading, PageHeader, Select, Tabs } from '@/components/ui';
import { ProductStatusBadge } from './products';

type Tab = 'draws' | 'upcoming' | 'versions' | 'exceptions';

export function ProductPage() {
  const t = useT();
  const code = (useParams().code ?? '').toUpperCase();
  const nav = useNavigate();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const [tab, setTab] = useState<Tab>('draws');
  const [viewing, setViewing] = useState<number | null>(null);
  const detail = useQuery({ queryKey: ['product', code], queryFn: () => get<ProductDetailView>(`/platform/products/${code}`) });
  const shown = viewing ?? detail.data?.publishedVersion ?? null;
  const version = useQuery({ queryKey: ['product', code, 'version', shown], queryFn: () => get<ProductVersionView>(`/platform/products/${code}/versions/${shown}`), enabled: shown != null });
  const upcoming = useQuery({ queryKey: ['product', code, 'schedule'], queryFn: () => get<{ timezone: string; draws: PlannedDraw[] }>(`/platform/products/${code}/schedule?days=14`), enabled: tab === 'upcoming' && !!detail.data?.publishedVersion });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['product', code] });
    void qc.invalidateQueries({ queryKey: ['products'] });
  };
  const newDraft = useMutation({ mutationFn: () => post<ProductVersionView>(`/platform/products/${code}/versions`), onSuccess: (v) => nav(`/products/${code}/versions/${v.version}/edit`) });
  const status = useMutation({ mutationFn: (s: 'active' | 'inactive') => patch(`/platform/products/${code}`, { status: s }), onSuccess: refresh });
  const [renaming, setRenaming] = useState(false);
  const rename = useMutation({
    mutationFn: (n: { nameEn: string; nameKm: string }) => patch(`/platform/products/${code}`, n),
    onSuccess: () => {
      setRenaming(false);
      refresh();
    },
  });

  if (detail.isLoading) return <Loading />;
  if (detail.error || !detail.data) return <ErrorBox text={errorText(detail.error)} />;
  const p = detail.data;

  return (
    <>
      <Link to="/products" className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {t('All products')}
      </Link>
      <PageHeader
        title={p.nameEn}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{p.code}</span>
            <span>{p.nameKm}</span>
            <ProductStatusBadge status={p.status} />
            {p.publishedVersion && <Badge>v{p.publishedVersion}</Badge>}
          </span>
        }
        actions={
          superAdmin && (
            <>
              <Button variant="secondary" icon={<Pencil size={14} />} onClick={() => setRenaming(true)}>
                {t('Rename')}
              </Button>
              <Button variant="secondary" onClick={() => status.mutate(p.status === 'active' ? 'inactive' : 'active')} disabled={status.isPending}>
                {t(p.status === 'active' ? 'Make inactive' : 'Make active')}
              </Button>
              {p.draftVersion ? (
                <Button icon={<Pencil size={14} />} onClick={() => nav(`/products/${code}/versions/${p.draftVersion}/edit`)}>
                  {t('Edit draft')} v{p.draftVersion}
                </Button>
              ) : (
                <Button icon={<Plus size={14} />} onClick={() => newDraft.mutate()} disabled={newDraft.isPending}>
                  {t('New version')}
                </Button>
              )}
            </>
          )
        }
      />
      {(newDraft.error || status.error) && <ErrorBox text={errorText(newDraft.error ?? status.error)} />}
      {renaming && (
        <RenameDialog
          title={`${t('Rename')} ${p.code}`}
          nameEn={p.nameEn}
          nameKm={p.nameKm}
          hint={t('The code stays. Clients with their own name for this product keep it.')}
          busy={rename.isPending}
          error={rename.error}
          onSave={(n) => rename.mutate(n)}
          onClose={() => setRenaming(false)}
        />
      )}

      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        items={[
          { value: 'draws', label: t('Draws') },
          { value: 'upcoming', label: t('Next 14 days') },
          { value: 'versions', label: `${t('Versions')} (${p.versions.length})` },
          { value: 'exceptions', label: `${t('Exceptions')} (${p.exceptions.length})` },
        ]}
      />

      {tab === 'draws' && (
        <Card title={shown ? `${t('Version')} ${shown}${version.data?.status === 'draft' ? ` · ${t('Draft')}` : ''}` : t('Draws')} actions={viewing && <Button variant="ghost" onClick={() => setViewing(null)}>{t('Back to the published version')}</Button>}>
          {!shown ? <p className="text-sm text-ink-600">{t('No published version yet.')}</p> : version.isLoading ? <Loading /> : version.data ? <ProductConfigView config={version.data.config} /> : <ErrorBox text={errorText(version.error)} />}
        </Card>
      )}

      {tab === 'upcoming' && (
        <Card title={t('Next 14 days')}>{upcoming.isLoading ? <Loading /> : upcoming.error ? <ErrorBox text={errorText(upcoming.error)} /> : upcoming.data ? <PlannedDraws draws={upcoming.data.draws} timezone={upcoming.data.timezone} /> : null}</Card>
      )}

      {tab === 'versions' && (
        <Card title={t('Versions')}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs text-ink-500">
                <th className="py-1.5 pr-3">{t('Version')}</th>
                <th className="py-1.5 pr-3">{t('State')}</th>
                <th className="py-1.5 pr-3">{t('Note')}</th>
                <th className="py-1.5 pr-3">{t('Published')}</th>
                <th className="py-1.5 pr-3 text-right">{t('Clients')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {p.versions.map((v) => (
                <tr key={v.version} className="border-b border-ink-100 last:border-0">
                  <td className="py-2 pr-3 font-mono">v{v.version}</td>
                  <td className="py-2 pr-3">{v.status === 'draft' ? <Badge tone="blue">{t('Draft')}</Badge> : <Badge tone="green">{t('Published')}</Badge>}</td>
                  <td className="py-2 pr-3 text-xs text-ink-600">{v.note ?? '—'}</td>
                  <td className="py-2 pr-3 text-xs">{dateTime(v.publishedAt)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{v.clients}</td>
                  <td className="py-2 text-right">
                    {v.status === 'draft' && superAdmin ? (
                      <Button variant="ghost" onClick={() => nav(`/products/${code}/versions/${v.version}/edit`)}>
                        {t('Edit')}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setViewing(v.version);
                          setTab('draws');
                        }}
                      >
                        {t('View')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-ink-500">{t('A published version never changes. A client stays on its version until it is moved on its page.')}</p>
        </Card>
      )}

      {tab === 'exceptions' && <Exceptions detail={p} superAdmin={superAdmin} gameCodes={version.data?.config.games.map((g) => g.code) ?? []} onChange={refresh} />}
    </>
  );
}

function Exceptions({ detail, superAdmin, gameCodes, onChange }: { detail: ProductDetailView; superAdmin: boolean; gameCodes: string[]; onChange: () => void }) {
  const t = useT();
  const [gameCode, setGameCode] = useState(gameCodes[0] ?? '');
  const [date, setDate] = useState('');
  const [action, setAction] = useState<DrawExceptionAction>('move');
  const [toDate, setToDate] = useState('');
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');
  const add = useMutation({
    mutationFn: () => post(`/platform/products/${detail.code}/exceptions`, { gameCode: gameCode || gameCodes[0], date, action, ...(toDate && action === 'move' ? { toDate } : {}), ...(time ? { time } : {}), ...(note ? { note } : {}) }),
    onSuccess: () => {
      setDate('');
      setToDate('');
      setTime('');
      setNote('');
      onChange();
    },
  });
  const remove = useMutation({ mutationFn: (id: number) => del(`/platform/products/${detail.code}/exceptions/${id}`), onSuccess: onChange });
  const label = { cancel: t('Cancelled'), move: t('Moved'), extra: t('Extra') } as const;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  return (
    <Card title={t('Exceptions')}>
      <p className="mb-3 text-xs text-ink-500">{t('A draw cancelled, moved to another day, or an extra draw — for holidays. It applies to every client with the product, whatever its version.')}</p>
      {detail.exceptions.length === 0 ? (
        <p className="mb-3 text-sm text-ink-600">{t('Nothing yet.')}</p>
      ) : (
        <ul className="mb-4 divide-y divide-ink-100 text-sm">
          {detail.exceptions.map((e) => (
            <li key={e.id} className="flex flex-wrap items-center gap-3 py-2">
              <Badge tone="blue">{label[e.action]}</Badge>
              <span className="font-mono">{e.gameCode}</span>
              <span>{e.date}</span>
              {e.toDate && <span>→ {e.toDate}</span>}
              {e.time && <span className="font-mono">{e.time}</span>}
              {e.note && <span className="text-xs text-ink-500">{e.note}</span>}
              {superAdmin && (
                <button className="ml-auto text-ink-400 hover:text-red-600" onClick={() => remove.mutate(e.id)} aria-label={t('Remove')} title={t('Remove')}>
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {superAdmin && (
        <form onSubmit={submit} className="grid gap-3 border-t border-ink-100 pt-3 sm:grid-cols-3 lg:grid-cols-6">
          <Field label={t('Game')}>
            <Select value={gameCode} onChange={(e) => setGameCode(e.target.value)} className="w-full">
              {gameCodes.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('What')}>
            <Select value={action} onChange={(e) => setAction(e.target.value as DrawExceptionAction)} className="w-full">
              {DRAW_EXCEPTION_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {label[a]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('Date')}>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </Field>
          {action === 'move' && (
            <Field label={t('New date')}>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} required />
            </Field>
          )}
          {action !== 'cancel' && (
            <Field label={action === 'extra' ? t('Time') : t('New time (optional)')}>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} required={action === 'extra'} />
            </Field>
          )}
          <Field label={t('Note')}>
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
          </Field>
          <div className="flex items-end">
            <Button type="submit" disabled={add.isPending}>
              {t('Add')}
            </Button>
          </div>
          {add.error && (
            <div className="sm:col-span-3 lg:col-span-6">
              <ErrorBox text={[errorText(add.error), ...fieldProblems(add.error)].join(' — ')} />
            </div>
          )}
        </form>
      )}
    </Card>
  );
}
